// Libraries
import { Box, Button, Flex, Modal, ModalContent, ModalOverlay, Text } from '@chakra-ui/react';
import { Fragment, cloneElement, forwardRef, useImperativeHandle, useState } from 'react';
// Components
import { ExpandedChevronIcon } from '@atoms/index';
// Types
import { CollapsibleMenuHandle, CollapsibleMenuProps } from './CollapsibleMenu.types';

/**
 * `CollapsibleMenu` component provides a UI interface for a collapsible section,
 * which can be toggled between expanded and collapsed states with a button.
 *
 * @component
 * @param {CollapsibleMenuProps} props - The props to configure the CollapsibleMenu component.
 * @param {string} props.buttonLabel - The label displayed on the toggle button.
 * @param {ReactNode} props.children - The content inside the collapsible section that will be displayed when expanded.
 *
 * @returns {ReactElement} The rendered CollapsibleMenu component.
 */
export const CollapsibleMenu = forwardRef<CollapsibleMenuHandle, CollapsibleMenuProps>(
  ({ buttonLabel, children }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const transform = isOpen ? 'rotate(180deg)' : '';

    const handleClick = () => setIsOpen((s) => !s);

    useImperativeHandle(
      ref,
      () => ({
        toggle: () => setIsOpen(!isOpen),
        setIsOpen: (status) => setIsOpen(status),
      }),
      [isOpen]
    );

    return (
      <Box
        position={isOpen ? 'absolute' : 'sticky'}
        borderTopLeftRadius='xl'
        borderTopRightRadius='xl'
        bottom={0}
        w='full'
        boxShadow='floatingButton'
        zIndex='sticky'
      >
        <Flex direction='column' borderTopLeftRadius='xl' borderTopRightRadius='xl' overflow='hidden'>
          <Button
            onClick={handleClick}
            w='full'
            h={15}
            borderBottom='section'
            borderRadius='none'
            bg='white'
            _hover={{ bg: 'white' }}
          >
            <Flex direction='column' w='full' h='full' align='center' justify='space-between' p={1.5}>
              <ExpandedChevronIcon w={12} transform={transform} />
              <Text flex={1} fontSize='l' fontWeight='semibold' lineHeight='taller'>
                {buttonLabel}
              </Text>
            </Flex>
          </Button>
          <Modal onClose={() => setIsOpen(false)} isOpen={isOpen}>
            <ModalOverlay />
            <ModalContent maxW='full' position='absolute' bottom={0} top={62} my={0}>
              <Fragment>
                <Button
                  onClick={handleClick}
                  w='full'
                  h={15}
                  borderBottom='section'
                  borderRadius='none'
                  bg='white'
                  borderTopLeftRadius='xl'
                  borderTopRightRadius='xl'
                  boxShadow='floatingButton'
                  _hover={{ bg: 'white' }}
                >
                  <Flex direction='column' w='full' h='full' align='center' justify='space-between' p={1.5}>
                    <ExpandedChevronIcon w={12} transform={transform} />
                    <Text flex={1} fontSize='l' fontWeight='semibold' lineHeight='taller'>
                      {buttonLabel}
                    </Text>
                  </Flex>
                </Button>
                {cloneElement(children, { setIsOpen })}
              </Fragment>
            </ModalContent>
          </Modal>
        </Flex>
      </Box>
    );
  }
);
